import { ApplicationResponseStatus } from 'generatedTypes/flight/models';
import { hectaresToAcres } from 'utils/areaUtil';
import { formatTo12Hour, formatToUSDate } from 'utils/dateTimeUtils/dateFormatUtil';
import { calcDurationMinutes } from 'utils/dateTimeUtils/timeUtil';
import { computeFlightTotalsFromTelemetry } from 'utils/telemetryTotalsUtil';
import { litersToGallons } from 'utils/volumeUtil';

import { InfiniteScroll, Wrapper } from '@american-autonomy/ag-ui';

import Timeline from 'components/Timeline';
import TimelineItem from 'components/TimelineItem';
import SkeletonText from 'components/loaders/SkeletonText';
import ActivityTimeText from 'components/text/ActivityTimeText';
import CaptionText from 'components/text/CaptionText';
import TableHeaderText from 'components/text/TableHeaderText';

import useTranslation from './hooks/useTranslation';

import { COMPLETE_STATUS, START_STATUS, TEST_ID } from './constants';
import type { FlightResponse, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const formatDate = (date: Date | string): string =>
    formatToUSDate(typeof date === 'string' ? new Date(date) : date);

const formatTime = (date: Date | string): string =>
    formatTo12Hour(typeof date === 'string' ? new Date(date) : date);

const ApplicationActivityList: FunctionComponent<Props> = ({
    applicationStatus,
    className = '',
    fetchNextPage,
    flights,
    hasNextPage,
    isLoading,
    onSelectFlight,
    selectedFlightId,
    telemetryPointsByFlightId,
    testId = TEST_ID
}) => {
    const {
        ACRES,
        APPLICATION_COMPLETE,
        APPLICATION_STARTED,
        DURATION,
        FLIGHT,
        GALLONS,
        MINUTES,
        NO_ACTIVITY
    } = useTranslation();

    const firstFlight: FlightResponse | undefined = flights[0];
    const lastFlight: FlightResponse | undefined = flights[flights.length - 1];
    const isComplete = applicationStatus === ApplicationResponseStatus.COMPLETE;
    const isEmpty = !isLoading && flights.length === 0;
    const emptyClass = isEmpty ? styles.emptyState : '';

    const getDisplayTotals = (flight: FlightResponse) => {
        const hasServerTotals = flight.totalAreaHectares > 0 || flight.totalVolumeAppliedLiters > 0;

        if (hasServerTotals) {
            return {
                totalAreaHectares: flight.totalAreaHectares,
                totalVolumeAppliedLiters: flight.totalVolumeAppliedLiters
            };
        }

        const points = telemetryPointsByFlightId?.[flight.id] ?? [];

        return computeFlightTotalsFromTelemetry({
            fallbackSwathMeters: flight.swathWidthMeters,
            points
        });
    };

    return (
        <Wrapper
            className={`${emptyClass} ${className} ${styles.applicationActivityList}`}
            testId={testId}>
            {isEmpty ? (
                <CaptionText text={NO_ACTIVITY} />
            ) : (
                <Timeline>
                    {firstFlight && (
                        <TimelineItem status={START_STATUS}>
                            <TableHeaderText text={APPLICATION_STARTED} />

                            <ActivityTimeText
                                text={`${formatDate(firstFlight.startTime)} · ${formatTime(firstFlight.startTime)}`}
                            />
                        </TimelineItem>
                    )}

                    <InfiniteScroll
                        fetchNextPage={fetchNextPage}
                        hasNextPage={hasNextPage}
                        loader={<SkeletonText lines={2} />}>
                        {flights.map((flight, index) => {
                            const { totalAreaHectares, totalVolumeAppliedLiters } =
                                getDisplayTotals(flight);

                            return (
                                <TimelineItem
                                    isSelected={flight.id === selectedFlightId}
                                    key={flight.id}
                                    onClick={() => onSelectFlight?.(flight)}>
                                    <TableHeaderText
                                        text={`${FLIGHT} ${index + 1} (${DURATION}: ${calcDurationMinutes(flight.startTime, flight.endTime)} ${MINUTES})`}
                                    />

                                    <ActivityTimeText
                                        text={`${formatTime(flight.startTime)} · ${hectaresToAcres(totalAreaHectares)} ${ACRES} · ${litersToGallons(totalVolumeAppliedLiters)} ${GALLONS}`}
                                    />
                                </TimelineItem>
                            );
                        })}
                    </InfiniteScroll>

                    {isComplete && lastFlight && !hasNextPage && (
                        <TimelineItem status={COMPLETE_STATUS}>
                            <TableHeaderText text={APPLICATION_COMPLETE} />

                            <ActivityTimeText
                                text={`${formatDate(lastFlight.endTime)} · ${formatTime(lastFlight.endTime)}`}
                            />
                        </TimelineItem>
                    )}
                </Timeline>
            )}
        </Wrapper>
    );
};

export default ApplicationActivityList;
